import type { ReactNode } from 'react';
import MarkdownRenderer from '@/components/common/MarkdownRenderer';
import { getModuleIcon } from '@/utils/moduleIcons';

interface SectionProps {
    title?: string;
    subtitle?: string;
    children: ReactNode;
    className?: string;
    accent?: 'purple' | 'blue' | 'muted' | 'none';
    iconKey?: string;
    iconClassName?: string;
    showBorder?: boolean;
}

const accentMap: Record<string, string> = {
    purple: 'bg-purple-100 border-purple-200',
    blue: 'bg-blue-100 border-blue-200',
    muted: 'bg-panel border-neutral-300',
    none: 'bg-neutral-0 border-neutral-300',
};

const accentIconColor: Record<string, string> = {
    purple: 'text-purple-600',
    blue: 'text-blue-600',
    muted: 'text-neutral-600',
    none: 'text-neutral-600',
};

const accentTitleColor: Record<string, string> = {
    purple: 'text-foreground-static',
    blue: 'text-foreground-static',
    muted: 'text-neutral-900',
    none: 'text-neutral-900',
};

const accentSubtitleColor: Record<string, 'muted-foreground' | 'neutral-500'> = {
    purple: 'neutral-500',
    blue: 'neutral-500',
    muted: 'muted-foreground',
    none: 'muted-foreground',
};

export default function Section({
    title,
    subtitle,
    children,
    className = '',
    accent = 'none',
    iconKey,
    iconClassName = '',
    showBorder = true,
}: SectionProps) {
    const IconComp = iconKey ? getModuleIcon(iconKey) : null;
    const iconColor = accentIconColor[accent];
    const titleColor = accentTitleColor[accent];
    const subtitleColor = accentSubtitleColor[accent];

    return (
        <section className={`rounded-lg ${showBorder ? 'p-5' : ''} ${accentMap[accent]} ${className}`}>
            {(title || subtitle) && (
                <header>
                    <div className="pdf-avoid-break mb-4 flex flex-col">
                        <div className={`mb-2 ${IconComp ? 'flex items-center gap-3' : ''}`}>
                            {IconComp && (
                                <IconComp
                                    className={`h-5 w-5 shrink-0 ${iconColor} ${iconClassName}`}
                                    strokeWidth={2}
                                    aria-hidden="true"
                                />
                            )}
                            {title && <h3 className={`text-lg leading-none font-semibold ${titleColor}`}>{title}</h3>}
                        </div>

                        {subtitle && (
                            <div className="mt-1">
                                <MarkdownRenderer
                                    content={subtitle}
                                    variant="inline"
                                    color={subtitleColor}
                                    className="text-base leading-snug"
                                />
                            </div>
                        )}
                    </div>
                </header>
            )}
            {children}
        </section>
    );
}
